import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import config from "./services/config.json";
import { MessageSquare, PenSquare, Trash2, LogOut, Check, MoreVertical } from "lucide-react";
import { FiSidebar, FiDownload, FiAward } from "react-icons/fi";
import { cn } from "lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "components/ai-elements/message";
import {
  PromptInput,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
} from "components/ai-elements/prompt-input";
import { Shimmer } from "components/ai-elements/shimmer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";

import logoUrl from "./assets/iflet.png";

const ChatGPTCloneCleanUI = () => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [email, setEmail] = useState(sessionStorage.getItem("email"));
  const [userName, setUserName] = useState(sessionStorage.getItem("userName"));
  const [sessionMessages, setSessionMessages] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [conversationsExist, setConversationsExist] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [status, setStatus] = useState("ready"); // "ready", "submitted", "streaming"
  const [threads, setThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState("");
  const [editingThreadId, setEditingThreadId] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [threadToDelete, setThreadToDelete] = useState("");
  const [activeDropdownId, setActiveDropdownId] = useState("");
  const [isToastShowing, setIsToastShowing] = useState(false);
  
  const navigate = useNavigate();

  const fetchThreads = useCallback(async () => {
    try {
      const response = await axios.post(`${config.API_URL}/chat_threads`, { email }, { headers: config.headers });
      if (response.data && response.data.threads) {
        setThreads(response.data.threads);
      } else if (response.data && response.data.success) {
        // Fallback for if API wraps it
        const parsedData = JSON.parse(response.data.data);
        if (parsedData.threads) setThreads(parsedData.threads);
      }
    } catch (err) {
      console.error("Failed to fetch threads:", err);
    }
  }, [email]);

  const handleSignOut = () => {
    sessionStorage.removeItem("email");
    sessionStorage.removeItem("userName");
    navigate("/SignIn");
  };

  const ClearChat = () => {
    axios.post(
      `${config.API_URL}/chat_clear`,
      {
        email,
      },
      {
        headers: config.headers,
      }
    ).then((response) => {
      const message = response.data.message;
      toast.success(message);
      setMessages([]);
      setSessionMessages([]);
    }).catch((error) => {
      toast.error("Failed to clear chat!");
    });
  };

  const handleRenameThread = async (e: React.MouseEvent, threadId: string, currentTitle: string) => {
    e.stopPropagation();
    if (editingThreadId === threadId) {
      if (editTitle.trim() !== "" && editTitle !== currentTitle) {
        try {
          await axios.post(`${config.API_URL}/rename_thread`, {
            session_id: threadId,
            title: editTitle
          }, { headers: config.headers });
          
          setThreads(threads.map((t: any) => t.session_id === threadId ? { ...t, title: editTitle } : t));
          toast.success("Chat renamed successfully");
        } catch (error) {
          toast.error("Failed to rename chat");
        }
      }
      setEditingThreadId("");
    } else {
      setEditingThreadId(threadId);
      setEditTitle(currentTitle || "New Chat");
    }
  };

  const confirmDeleteThread = async () => {
    if (threadToDelete) {
      try {
        await axios.post(`${config.API_URL}/delete_thread`, {
          session_id: threadToDelete
        }, { headers: config.headers });
        
        const newThreads = threads.filter((t: any) => t.session_id !== threadToDelete);
        setThreads(newThreads);
        
        if (currentThreadId === threadToDelete) {
            setMessages([]);
            setSessionMessages([]);
            if (newThreads.length > 0) {
                const nextThreadId = newThreads[0].session_id;
                setCurrentThreadId(nextThreadId);
                loadThreadHistory(nextThreadId);
            } else {
                setCurrentThreadId("");
                LoadDefault();
            }
        }
        toast.success("Chat deleted successfully");
      } catch (error) {
        toast.error("Failed to delete chat");
      }
    }
    setThreadToDelete("");
  };

  const handleDeleteThread = async (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    setThreadToDelete(threadId);
  };
  
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, status]);

  // Clear input when switching chats
  useEffect(() => {
    if (textareaRef.current) {
        textareaRef.current.value = "";
    }
    setUserInput("");
  }, [currentThreadId]);
  
  const loadThreadHistory = useCallback(async (threadId) => {
    if (!threadId) return;
    try {
      const response = await axios.post(
        `${config.API_URL}/chat_history`,
        { session_id: threadId },
        { headers: config.headers }
      );

      if (response.status === 200 && response.data.data) {
        const parsedData = JSON.parse(response.data.data);
        const conversations = parsedData.conversations || [];

        if (conversations.length > 0) {
          const formattedConversations = conversations
            .map((conv) => [
              { sender: "user", text: conv.user },
              { sender: "ai", text: conv.ai },
            ])
            .flat();

          setMessages(formattedConversations);
          setSessionMessages(formattedConversations);
        } else {
          LoadDefault();
        }
      }
    } catch (error) {
      console.error("Error loading conversation history:", error);
      LoadDefault();
    }
  }, []);

  const LoadDefault = () => {
    axios
      .get(`${config.API_URL}/chat_default`, { headers: config.headers })
      .then((r) => {
        const defaultMessage = { sender: "ai", text: r.data.conversations };
        setMessages([defaultMessage]);
      });
  };

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  useEffect(() => {
    if (threads.length > 0 && !currentThreadId) {
      // Auto-load the latest thread on first load
      const latestThreadId = threads[0].session_id;
      setCurrentThreadId(latestThreadId);
      loadThreadHistory(latestThreadId);
    } else if (threads.length === 0 && !currentThreadId) {
      // New user, start fresh
      setCurrentThreadId(crypto.randomUUID());
      LoadDefault();
    }
  }, [threads, currentThreadId, loadThreadHistory]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Empty cleanup hook
  }, [email, messages, currentThreadId]);

  const handleNewConversation = () => {
    const newThreadId = crypto.randomUUID();
    setCurrentThreadId(newThreadId);
    
    // Optimistically add to threads list to indicate a new chat
    setThreads([{ session_id: newThreadId, title: "New Chat" }, ...threads]);
    
    // Show toast for new chat
    if (!isToastShowing) {
        setIsToastShowing(true);
        toast.info("New chat started", { onClose: () => setIsToastShowing(false), autoClose: 2000 });
    }
    
    setUserInput("");
    LoadDefault();
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(10, 146, 173);
    doc.text("Iflekt Chat Transcript", 14, 22);
    
    // Add date
    doc.setFontSize(11);
    doc.setTextColor(100);
    const dateStr = new Date().toLocaleString();
    doc.text(`Generated on: ${dateStr}`, 14, 30);
    
    // Prepare table data
    const tableColumn = ["Sender", "Message"];
    const tableRows = [];
    
    messages.forEach(msg => {
      let cleanText = msg.text.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      cleanText = cleanText.replace(/#### |### |## |# /g, '');
      
      const senderName = msg.sender === "user" ? (userName || "You") : "Iflekt";
      tableRows.push([senderName, cleanText]);
    });
    
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      styles: { fontSize: 10, cellPadding: 5, overflow: 'linebreak' },
      headStyles: { fillColor: [10, 146, 173], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold' },
        1: { cellWidth: 'auto' }
      },
      didParseCell: function (data) {
        if (data.section === 'body' && data.row.raw[0] === 'Iflekt') {
          data.cell.styles.fillColor = [240, 248, 255];
        }
      }
    });
    
    const filename = `Iflekt_Chat_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  const isStreamingRef = useRef(false);

  const handleSend = async (textToSend) => {
    if (!textToSend.trim()) return;

    const userMessage = { sender: "user", text: textToSend };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setSessionMessages((prevSession) => [...prevSession, userMessage]);
    setStatus("submitted");

    try {
      const sessionContext = sessionMessages.map((msg) => ({
        sender: msg.sender,
        text: msg.text,
      }));

      const response = await fetch(`${config.API_URL}/chat_ask`, {
        method: "POST",
        headers: config.headers,
        body: JSON.stringify({
          context: sessionContext,
          latestQuestion: textToSend,
          email,
          userName,
          session_id: currentThreadId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setStatus("streaming");
      isStreamingRef.current = true;

      const aiMessage = { sender: "ai", text: "" };
      setMessages((prevMessages) => [...prevMessages, aiMessage]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let currentText = "";
      let buffer = "";
      
      while (true) {
        if (!isStreamingRef.current) {
          reader.cancel();
          break;
        }
        
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        if (buffer.includes("data: ")) {
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep the last incomplete line in buffer
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.startsWith("data: ")) {
              const dataStr = line.slice(6).trim();
              if (dataStr === "[DONE]") continue;
              try {
                const data = JSON.parse(dataStr);
                
                if (data.new_title) {
                  setThreads((prevThreads: any) => 
                    prevThreads.map((t: any) => 
                      t.session_id === currentThreadId ? { ...t, title: data.new_title } : t
                    )
                  );
                }

                const token = data.text || data.choices?.[0]?.delta?.content || data.aiMessage || "";
                currentText += token;
              } catch (e) {
                currentText += (currentText.length > 0 && !currentText.endsWith("\n") ? "\n" : "") + dataStr;
              }
            } else if (line.trim() !== "") {
              // Line without 'data:' prefix - restore dropped newlines/text
              currentText += (currentText.length > 0 && !currentText.endsWith("\n") ? "\n" : "") + line;
            }
          }
          // Real SSE streaming updates UI instantly
          setMessages((prevMessages) => {
            const newMessages = [...prevMessages];
            newMessages[newMessages.length - 1] = { sender: "ai", text: currentText };
            return newMessages;
          });
        } else {
          // Raw text
          currentText += buffer;
          buffer = "";
          
          setMessages((prevMessages) => {
            const newMessages = [...prevMessages];
            newMessages[newMessages.length - 1] = { sender: "ai", text: currentText };
            return newMessages;
          });
        }
      }
      
      if (buffer.trim()) {
         currentText += buffer.replace(/^data:\s*/, "");
         setMessages((prevMessages) => {
            const newMessages = [...prevMessages];
            newMessages[newMessages.length - 1] = { sender: "ai", text: currentText };
            return newMessages;
          });
      }

      // Fallback: If currentText is actually a full JSON blob (backend isn't streaming yet)
      try {
        const fallbackJson = JSON.parse(currentText);
        
        if (fallbackJson && fallbackJson.new_title) {
            setThreads((prevThreads: any) => 
              prevThreads.map((t: any) => 
                t.session_id === currentThreadId ? { ...t, title: fallbackJson.new_title } : t
              )
            );
        }

        if (fallbackJson && fallbackJson.data && fallbackJson.data.aiMessage) {
            const fullAiMessage = fallbackJson.data.aiMessage;
            currentText = ""; // Reset for simulation
            
            // Simulate typing at a slower, realistic reading speed
            const chars = fullAiMessage.split("");
            for (let i = 0; i < chars.length; i++) {
              if (!isStreamingRef.current) break;
              currentText += chars[i];
              
              // Update state every 2 characters or on spaces for a smooth but slower feel
              if (i % 2 === 0 || chars[i] === ' ' || i === chars.length - 1) {
                setMessages((prevMessages) => {
                  const newMessages = [...prevMessages];
                  newMessages[newMessages.length - 1] = { sender: "ai", text: currentText };
                  return newMessages;
                });
                await new Promise(r => setTimeout(r, 20)); // Slower 20ms delay for readability
              }
            }
        }
      } catch (e) {
        // Not a JSON blob, meaning it was a true stream. Do nothing.
      }

      // Check for misconceptions after the stream is fully finished
      const misconceptionRegex = /\[MISCONCEPTION:\s*(.*?)\]/i;
      const match = currentText.match(misconceptionRegex);
      
      if (match && match[1]) {
        const misconceptionData = {
          misconception_id: crypto.randomUUID(),
          learner_id: email,
          topic_id: null,
          tag: match[1].trim(),
          frequency: 1,
          last_seen: new Date().toISOString()
        };
        fetch(`${config.API_URL}/log_misconception`, {
          method: 'POST',
          headers: {
            ...config.headers,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(misconceptionData),
          keepalive: true
        }).catch(err => console.error("Failed to log misconception:", err));
        
        currentText = currentText.replace(misconceptionRegex, "").trim();
        
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          newMessages[newMessages.length - 1] = { sender: "ai", text: currentText };
          return newMessages;
        });
      }

      setSessionMessages((prevSession) => [...prevSession, { sender: "ai", text: currentText }]);
      
      // Refresh threads to get the new generated title if this was a new thread
      if (!threads.find(t => t.session_id === currentThreadId)) {
        fetchThreads();
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
      const errorMessage = {
        sender: "ai",
        text: "Sorry, something went wrong. Please try again later.",
      };

      setMessages((prevMessages) => [...prevMessages, errorMessage]);
      setSessionMessages((prevSession) => [...prevSession, errorMessage]);
    } finally {
        setStatus("ready");
        isStreamingRef.current = false;
    }
  };

  return (
    <div className="flex w-full h-svh bg-background font-sans overflow-hidden">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      {/* Sidebar - Copied from ChatSidebar.tsx */}
      <aside className={cn(
        "absolute md:relative z-50 h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 md:flex",
        isSidebarOpen ? "w-64 flex" : "w-0 hidden overflow-hidden border-none"
      )}>
        <div className="flex items-center gap-2 px-3 py-4">
          <img src={logoUrl} alt="Iflekt" className="h-6 w-auto" />
          <button 
             onClick={() => setIsSidebarOpen(false)} 
             className="ml-auto text-sidebar-foreground p-2 rounded-md hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all duration-200 cursor-pointer"
             title="Toggle Sidebar"
          >
             <FiSidebar size={18} />
          </button>
        </div>

        <div className="px-3">
          <button
            onClick={handleNewConversation}
            className="flex w-full items-center gap-2 rounded-lg border border-sidebar-border px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
          >
            <PenSquare className="size-4" />
            New chat
          </button>
        </div>

        <nav className="mt-4 flex-1 space-y-1 overflow-y-auto px-2 pb-4">
          {threads.length > 0 ? (
            threads.map((thread) => (
              <div 
                key={thread.session_id}
                onClick={() => {
                  setCurrentThreadId(thread.session_id);
                  loadThreadHistory(thread.session_id);
                }}
                className={`group flex items-center gap-2 rounded-lg px-2 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent cursor-pointer ${
                  currentThreadId === thread.session_id ? "bg-sidebar-accent" : ""
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2 py-2">
                  <MessageSquare className="size-4 shrink-0 opacity-60" />
                  {editingThreadId === thread.session_id ? (
                      <input 
                          type="text" 
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onKeyDown={(e: any) => {
                              if (e.key === 'Enter') handleRenameThread(e, thread.session_id, thread.title);
                              if (e.key === 'Escape') {
                                  setEditingThreadId("");
                                  e.stopPropagation();
                              }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 bg-transparent border-b border-sidebar-foreground/30 focus:outline-none text-sm text-sidebar-foreground px-1 py-0 w-full"
                          autoFocus
                      />
                  ) : (
                      <span className="truncate">{thread.title || "New Chat"}</span>
                  )}
                </div>
                <div className="relative flex items-center flex-shrink-0">
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdownId(activeDropdownId === thread.session_id ? "" : thread.session_id);
                        }} 
                        className="p-1 hover:text-sidebar-accent-foreground text-sidebar-foreground transition-colors cursor-pointer"
                        title="Chat Options"
                    >
                        <MoreVertical size={16} />
                    </button>
                    {activeDropdownId === thread.session_id && (
                        <div 
                            className="absolute right-0 top-6 mt-1 w-28 bg-popover rounded-md shadow-lg border border-border z-50 py-1 flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={(e) => {
                                    handleRenameThread(e, thread.session_id, thread.title);
                                    setActiveDropdownId("");
                                }}
                                className="px-3 py-1.5 text-sm text-popover-foreground hover:bg-accent text-left flex items-center gap-2"
                            >
                                <PenSquare size={14} /> Rename
                            </button>
                            <button
                                onClick={(e) => {
                                    handleDeleteThread(e, thread.session_id);
                                    setActiveDropdownId("");
                                }}
                                className="px-3 py-1.5 text-sm text-red-500 hover:bg-accent text-left flex items-center gap-2"
                            >
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    )}
                </div>
              </div>
            ))
          ) : (
            <div className="group flex items-center gap-2 rounded-lg px-2 text-sm text-sidebar-foreground transition-colors">
              <div className="flex min-w-0 flex-1 items-center gap-2 py-2">
                <span className="truncate opacity-60">No past conversations</span>
              </div>
            </div>
          )}
        </nav>

        <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete("")}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your chat history and remove it from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteThread} className="bg-red-600 hover:bg-red-700 text-white">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="mt-auto flex flex-col gap-1 border-t border-sidebar-border p-3">
          {userName && (
              <div className="px-3 py-2 text-sm text-sidebar-foreground mb-2 truncate flex flex-col gap-0.5">
                  <span className="font-semibold text-foreground">{userName}</span>
                  <span className="text-xs opacity-70">{email}</span>
              </div>
          )}
          {/* <button
            onClick={() => navigate("/dashboard")}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
          >
            <FiAward className="size-4" />
            My Progress
          </button> */}
          
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
          >
            <LogOut className="size-4" />
            Sign out
          </button>
        </div>

        <p className="border-t border-sidebar-border px-4 py-3 text-xs text-muted-foreground text-center">
          Chats stay in this session only.
        </p>
      </aside>

      {/* Main Chat Area - Copied from ChatWindow.tsx */}
      <main className="flex h-full flex-1 flex-col bg-background relative overflow-hidden">
        <header className="flex items-center gap-2 border-b border-border px-4 py-3 md:hidden">
          {!isSidebarOpen && (
            <button 
                className="text-foreground p-1 -ml-1 rounded-md hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all duration-200 cursor-pointer"
                onClick={() => setIsSidebarOpen(true)}
                title="Toggle Sidebar"
            >
                <FiSidebar size={18} />
            </button>
          )}
          <img src={logoUrl} alt="Iflekt" className="h-5 w-auto" />
        </header>
        
        {!isSidebarOpen && (
            <button 
                className="hidden md:block absolute top-4 left-4 z-10 text-foreground p-2 rounded-md hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all duration-200 cursor-pointer bg-background/50 backdrop-blur-sm border border-border"
                onClick={() => setIsSidebarOpen(true)}
                title="Toggle Sidebar"
            >
                <FiSidebar size={18} />
            </button>
        )}

        {messages.length > 0 && (
            <button 
                onClick={exportToPDF} 
                className="absolute top-4 right-4 z-10 flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hidden md:flex"
            >
                <FiDownload size={14} /> Export to PDF
            </button>
        )}

        <Conversation className="flex-1">
          <ConversationContent className="mx-auto w-full max-w-3xl gap-6 px-4 py-8">
            {messages.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-6 py-16 text-center">
                <img src={logoUrl} alt="Iflekt" className="h-16 w-auto" />
                <h1 className="font-display text-2xl font-semibold text-foreground mb-4">
                  Welcome to Iflekt
                </h1>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto">
                  I am your AI Coach and I’m so glad you’re here. I want to acknowledge you for taking this step to invest in yourself. As your coach, my role isn’t just to hand you advice or fix things, but to partner with you, challenge your thinking, and help you map out your own path forward.
                </p>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mt-4">
                  Everything we discuss is completely confidential and a total judgment-free zone. That’s enough about me, though—I want to hand the microphone over to you.
                </p>
                <p className="text-foreground text-base font-medium leading-relaxed max-w-2xl mx-auto mt-6">
                  What brings you to coaching right now, and what would make our time today a massive win for you?
                </p>
              </div>
            ) : (
              messages.map((message, index) => {
                const role = message.sender === "user" ? "user" : "assistant";
                return (
                  <Message from={role} key={index}>
                    <MessageContent
                      className={
                        role === "assistant"
                          ? "bg-transparent px-0 text-foreground"
                          : "bg-blue-600 text-white dark:bg-blue-700"
                      }
                    >
                        {role === "assistant" ? (
                            <MessageResponse>{message.text}</MessageResponse>
                        ) : (
                            <div className="whitespace-pre-wrap">{message.text}</div>
                        )}
                    </MessageContent>
                  </Message>
                );
              })
            )}

            {status === "submitted" && (
              <Message from="assistant">
                <MessageContent className="bg-transparent px-0">
                  <Shimmer>Thinking...</Shimmer>
                </MessageContent>
              </Message>
            )}
            <div ref={messagesEndRef} />
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>

        <div className="mx-auto w-full max-w-3xl px-4 pb-6">
          <PromptInput
            onSubmit={(message, event) => {
              event.preventDefault();
              handleSend(message.text ?? "");
              event.currentTarget.reset();
            }}
          >
            <PromptInputTextarea ref={textareaRef} placeholder="Message Iflekt..." className="text-base py-4 min-h-[6rem] pr-14" autoFocus />
            <PromptInputFooter className="justify-end">
              <PromptInputSubmit status={status as any} onStop={() => {
                setStatus("ready");
                isStreamingRef.current = false;
              }} />
            </PromptInputFooter>
          </PromptInput>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Iflekt can make mistakes. Double-check important info.
          </p>
        </div>
      </main>

      {/* Popup Overlay Removed */}
    </div>
  );
};

export default ChatGPTCloneCleanUI;
